import { ComponentType, ElementType, lazy, Suspense } from 'react';
import { SuspenseLoadingScreen } from 'src/components/loading-screen/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: Record<string, unknown>) =>
  (
    <Suspense fallback={<SuspenseLoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );

// ----------------------------------------------------------------------

// Retry lazy loading when an error occurs
export const retryLazy = (componentImport: () => Promise<{ default: ComponentType }>) =>
  lazy(async () => {
    const pageAlreadyRefreshed = JSON.parse(
      window.localStorage.getItem('pageRefreshed') || 'false',
    );
    try {
      const component = await componentImport();
      window.localStorage.setItem('pageRefreshed', 'false');
      return component;
    } catch (error) {
      if (!pageAlreadyRefreshed) {
        window.localStorage.setItem('pageRefreshed', 'true');
        window.location.reload();
        // reload가 완료되기 전까지 타입스크립트를 만족시키기 위해 throw
        throw new Error('Reloading page');
      }
      throw error;
    }
  });

// ===== Common =====
export const Page403 = Loadable(lazy(() => import('../screens/common/Page403')));
export const Page404 = Loadable(lazy(() => import('../screens/common/Page404')));
export const Page500 = Loadable(lazy(() => import('../screens/common/Page500')));
export const PageError = Loadable(retryLazy(() => import('../screens/common/PageError')));

// ===== Home =====
export const HomeV2 = Loadable(lazy(() => import('../screens/home/HomeV2')));

// ===== Auth =====
export const Login = Loadable(lazy(() => import('../screens/auth/login/Login')));
export const FindId = Loadable(lazy(() => import('../screens/auth/find-id/FindId')));
export const FindIdEnd = Loadable(lazy(() => import('../screens/auth/find-id/FindIdEnd')));
export const ResetPwd = Loadable(lazy(() => import('../screens/auth/reset-pwd/ResetPwd')));
export const ResetPwdForm = Loadable(lazy(() => import('../screens/auth/reset-pwd/ResetPwdForm')));
export const ResetPwdEnd = Loadable(lazy(() => import('../screens/auth/reset-pwd/ResetPwdEnd')));
export const Register = Loadable(lazy(() => import('../screens/auth/register/Register')));
export const RegisterEnd = Loadable(lazy(() => import('../screens/auth/register/RegisterEnd')));
export const ProfileCompletion = Loadable(
  lazy(() => import('../screens/auth/profile-completion/ProfileCompletion')),
);

// ===== Medical / Drug (보존: GLP-1에서도 사용) =====
export const MedicalQuestion = Loadable(
  lazy(() => import('../screens/medical-question/MedicalQuestion')),
);
export const MedicalQuestionForm = Loadable(
  lazy(() => import('../screens/medical-question/MedicalQuestionForm')),
);
export const MedicalQuestionEnd = Loadable(
  lazy(() => import('../screens/medical-question/MedicalQuestionEnd')),
);
export const DrugQuestion = Loadable(lazy(() => import('../screens/drug-question/DrugQuestion')));
export const DrugQuestionForm = Loadable(
  lazy(() => import('../screens/drug-question/DrugQuestionForm')),
);
export const MedicineSettings = Loadable(
  lazy(() => import('../screens/medicine-settings/MedicineSettings')),
);

// ===== Calendar =====
export const Calendar = Loadable(lazy(() => import('../screens/calendar/Calendar')));

// ===== GLP-1 5탭 (record / report / care 는 W2~W3 placeholder) =====
export const Record = Loadable(retryLazy(() => import('../screens/record/Record')));
export const Report = Loadable(retryLazy(() => import('../screens/report/Report')));
export const Care = Loadable(retryLazy(() => import('../screens/care/Care')));

// ===== MyPage =====
export const MyPage = Loadable(lazy(() => import('../screens/my-page/MyPage')));
export const PharmacySearch = Loadable(
  lazy(() => import('../screens/my-page/pharmacy/PharmacySearch')),
);
export const MedicineInfo = Loadable(
  lazy(() => import('../screens/my-page/medicine-info/MedicineInfo')),
);
export const Privacy = Loadable(lazy(() => import('../screens/my-page/privacy/Privacy')));
export const CustomerService = Loadable(
  lazy(() => import('../screens/my-page/customer-service/CustomerService')),
);
export const AppInfo = Loadable(lazy(() => import('../screens/my-page/app-info/AppInfo')));
export const OpenSourceLicense = Loadable(
  lazy(() => import('../screens/my-page/app-info/OpenSourceLicense')),
);
export const EditProfile = Loadable(lazy(() => import('../screens/my-page/my-info/EditProfile')));
export const EditMemberInfo = Loadable(
  lazy(() => import('../screens/my-page/my-info/EditMemberInfo')),
);
export const TermsSettings = Loadable(
  lazy(() => import('../screens/my-page/settings/terms-settings/TermsSettings')),
);
export const NotificationSettings = Loadable(
  lazy(() => import('../screens/my-page/settings/notification-settings/NotificationSettings')),
);
export const LanguageSettings = Loadable(
  lazy(() => import('../screens/my-page/settings/language-settings/LanguageSettings')),
);
export const PersonaSettings = Loadable(
  lazy(() => import('../screens/my-page/settings/persona-settings/PersonaSettings')),
);
export const Faq = Loadable(lazy(() => import('../screens/my-page/customer-service/faq/Faq')));
export const Notice = Loadable(
  lazy(() => import('../screens/my-page/customer-service/notice/Notice')),
);
export const Inquiry = Loadable(
  lazy(() => import('../screens/my-page/customer-service/inquiry/Inquiry')),
);
export const InquiryNew = Loadable(
  lazy(() => import('../screens/my-page/customer-service/inquiry/InquiryNew')),
);
export const Withdrawal = Loadable(lazy(() => import('../screens/my-page/withdrawal/Withdrawal')));

// ===== Notification / Terms =====
export const Terms = Loadable(lazy(() => import('../screens/terms/Terms')));
export const Notification = Loadable(lazy(() => import('../screens/notification/Notification')));
